/**
 * Small plans for the inspection report
 * (claude-docs/2026-10-15-inspection-report-design.md, "Plans"): the geometry
 * a plan needs, frozen into the report, and ONE drawing of it (`planSvg`) that
 * the public page and the PDF share, so the two cannot disagree.
 *
 * Every point is in the structure's aligned frame — the room outlines, a wall
 * face's `anchor.faceEnds` and a finding's `world` point all are — and is
 * drawn in the app's floor-plan screen convention: x right, z DOWN, so the top
 * of a plan is the smaller z. Pure.
 */
const { polygonOf } = require('./roomOutline');
const { isPointInsidePolygon } = require('../geometry/pointInPolygon');
const { deviceSvg } = require('./inspectionSymbols');
const { inFrame } = require('./inspectionStructure');
const {
  textBox,
  circleBox,
  placeLabels,
  ringCandidates,
  itemCandidates,
  slideCandidates,
} = require('./planLabels');
const tokens = require('../design-tokens/tokens.json').color;

// Colours from the design tokens as literal hex: an SVG presentation attribute
// cannot read var() in Safari, and the PDF draws this SVG onto a canvas.
const hex = (name) => {
  const v = tokens[name] && tokens[name].value;
  if (!/^#[0-9A-Fa-f]{6}$/.test(v || '')) {
    throw new Error(`design token color.${name} is not a hex colour`);
  }
  return v;
};
const COLORS = {
  room: hex('surface'),
  roomOther: hex('surface-soft'),
  outline: hex('text'),
  outlineOther: hex('border'),
  wall: hex('border-strong'),
  white: '#FFFFFF',
  // the fixed items and devices, light so the defect dots stand out
  fixture: hex('surface-raised'),
  cabinet: hex('border-muted'),
  counter: hex('surface-soft'),
  line: hex('text-subtle'),
  device: hex('text-subtle'),
  tag: hex('text-muted'),
  furniture: hex('border'),
  severity: {
    safety: hex('danger'),
    repair: hex('warn-text'),
    cosmetic: hex('text-muted'),
  },
};

const PAD_M = 0.6; // round the framed room(s), metres
const WALL_TAG_R = 9; // a wall's letter tag, on the wall, px
const WALL_TAG_OFF = WALL_TAG_R + 3; // its centre off the wall's face, px
const WALL_TAG_REACH = WALL_TAG_OFF + WALL_TAG_R + 1; // its far edge, px
const DOT_R = 8; // a finding's dot, px
const ITEM_TAG_PX = 9; // an item's tag, font px
const DEVICE_TAG_PX = 8; // a device's tag, font px
const DEVICE_TAG_AT = { dx: 9, dy: -8 }; // a device's tag, off the device, px
const DEVICE_MARK_R = 7; // what a device's symbol covers, round its point, px
const LEADER_PX = 16; // a device tag moved further than this gets a leader

const cm = (v) => Math.round(v * 100) / 100;
const point = (p) => ({ x: cm(p.x), z: cm(p.z) });
const f1 = (v) => v.toFixed(1);
const esc = (s) =>
  String(s).replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]
  );

function centroid(points) {
  const n = points.length;
  return points.reduce((c, p) => ({ x: c.x + p.x / n, z: c.z + p.z / n }), {
    x: 0,
    z: 0,
  });
}

/** A room's floor outline in cm-rounded plan points, or null when it has none. */
function roomOutline(room) {
  const poly = polygonOf(room);
  return poly && poly.length >= 3 ? poly.map(point) : null;
}

/** A wall-face doc a plan can draw: two face ends and a normal. */
function hasFace(doc) {
  const a = doc && doc.anchor;
  return Boolean(
    a && Array.isArray(a.faceEnds) && a.faceEnds.length === 2 && a.faceNormal
  );
}

/** 0 → "A", 25 → "Z", 26 → "AA". */
function letterFor(i) {
  const head = i >= 26 ? letterFor(Math.floor(i / 26) - 1) : '';
  return head + String.fromCharCode(65 + (i % 26));
}

/**
 * Letters for the wall faces the report names, clockwise on the plan from the
 * top. `faces`: wall-face docs that pass `hasFace`; `outline`: the room's, or
 * null (the faces' own ends then give the centre). → Map(targetId → letter)
 */
/**
 * How far round a point sits from straight up, clockwise on screen, about
 * `centre` — the order walls, items and devices are lettered and numbered in.
 * On screen z runs DOWN, so atan2(dz, dx) grows clockwise; shifted so straight
 * up is 0. → [0, 2π)
 */
function turnFromTop(point, centre) {
  return (
    (Math.atan2(point.z - centre.z, point.x - centre.x) +
      Math.PI / 2 +
      2 * Math.PI) %
    (2 * Math.PI)
  );
}

function letterWalls(faces, outline) {
  if (!faces.length) return new Map();
  const c = centroid(outline || faces.flatMap((d) => d.anchor.faceEnds));
  const turn = (d) => {
    const [a, b] = d.anchor.faceEnds;
    return turnFromTop({ x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 }, c);
  };
  const sorted = [...faces].sort(
    (a, b) =>
      turn(a) - turn(b) || String(a.targetId).localeCompare(String(b.targetId))
  );
  return new Map(sorted.map((d, i) => [d.targetId, letterFor(i)]));
}

/**
 * The key plan of one storey: every room's outline, named. A room's `room` is
 * the position of its part in the report, which a click on it opens on the
 * web; null for a room the report has no part for. `structure`: the storey's
 * walls, doors and windows (`inspectionStructure.storeyStructure`), or null.
 */
function keyPlan(storeyRooms, structure = null) {
  return {
    outlines: storeyRooms.map((r) => ({
      roomId: r.id,
      name: r.name,
      points: r.outline,
      focus: false,
      room: Number.isInteger(r.room) ? r.room : null,
    })),
    walls: [],
    markers: [],
    structure: structure || null,
  };
}

/** What a plan framed on `points` shows, in metres: their extent and the margin round it. */
function frameOf(points, pad = PAD_M) {
  return {
    minX: Math.min(...points.map((p) => p.x)) - pad,
    maxX: Math.max(...points.map((p) => p.x)) + pad,
    minZ: Math.min(...points.map((p) => p.z)) - pad,
    maxZ: Math.max(...points.map((p) => p.z)) + pad,
  };
}

/**
 * One room's plan: the room bold, its storey's other rooms faint, its named
 * walls lettered, a dot per numbered finding that has a `world` point.
 * `storeyRooms`: [{ id, name, outline }]; `walls`: [{ letter, doc }];
 * `findings`: [{ n, severity, world }].
 */
function roomPlan(roomId, storeyRooms, walls, findings, layers = {}) {
  const focus = storeyRooms.find((r) => r.id === roomId);
  return {
    outlines: storeyRooms.map((r) => ({
      roomId: r.id,
      name: r.name,
      points: r.outline,
      focus: r.id === roomId,
    })),
    walls: walls.map(({ letter, doc }) => ({
      letter,
      ends: doc.anchor.faceEnds.map(point),
      normal: point(doc.anchor.faceNormal),
    })),
    markers: findings
      .filter(
        (f) =>
          f.world && Number.isFinite(f.world.x) && Number.isFinite(f.world.z)
      )
      .map((f) => ({
        n: f.n,
        x: cm(f.world.x),
        z: cm(f.world.z),
        severity: f.severity,
      })),
    // `inspectionItems.roomSchedule`'s, drawn under the walls and the dots:
    // items [{ key, tag, point, boxes: [{ frame, corners, object }] }],
    // devices [{ key, tag, letters, type, subtype, normal, x, z }],
    // furniture [{ corners }]; `floorY` the room's floor height, metres
    floorY: Number.isFinite(layers.floorY) ? layers.floorY : null,
    items: layers.items || [],
    devices: layers.devices || [],
    furniture: layers.furniture || [],
    // the storey's walls, doors and windows that reach the plan's frame
    structure:
      focus && layers.structure
        ? inFrame(layers.structure, frameOf(focus.outline))
        : null,
  };
}

/**
 * Dots that would overlap, fanned out round their shared centre so every
 * number stays readable. In px: each `{ tx, tz }` (its true point) gains
 * `{ px, pz }` (where it is drawn).
 */
function fanOut(dots) {
  const groups = [];
  for (const d of dots) {
    const near = groups.find((g) =>
      g.some((o) => Math.hypot(o.tx - d.tx, o.tz - d.tz) < 2 * DOT_R)
    );
    if (near) near.push(d);
    else groups.push([d]);
  }
  return groups.flatMap((g) => {
    if (g.length === 1) return [{ ...g[0], px: g[0].tx, pz: g[0].tz }];
    const c = centroid(g.map((d) => ({ x: d.tx, z: d.tz })));
    // adjacent dots on a ring of radius r sit 2·r·sin(π/k) apart
    const r = (DOT_R + 1) / Math.sin(Math.PI / g.length);
    return g.map((d, i) => {
      const a = -Math.PI / 2 + (2 * Math.PI * i) / g.length;
      return { ...d, px: c.x + r * Math.cos(a), pz: c.z + r * Math.sin(a) };
    });
  });
}

/**
 * Where each wall letter, item tag and device tag of a plan is drawn, in px,
 * so no two sit on one another (`planLabels`). The wall letters go first:
 * each outside the room at its wall's midpoint — or, for a partition standing
 * inside the room (a step back across the face is still in the room), on the
 * face's own side, so the two faces' letters do not share one spot — then
 * slid along its wall when another letter, a finding dot or a device is
 * there. Then the item tags and
 * the device tags, clear of the letters, the finding dots and every OTHER
 * device's symbol. → { walls: [{ x, y }], items: [{ x, y } | null],
 * devices: [{ x, y, moved }] }, each in the plan's order.
 */
function labelSpots(plan, { X, Z, s, w, h, room, dots }) {
  const frame = { w, h };
  const walls = (plan.walls || []).map((wl) => {
    const [a, b] = wl.ends;
    const mid = { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 };
    const back = WALL_TAG_OFF / s;
    const behind = {
      x: mid.x - wl.normal.x * back,
      z: mid.z - wl.normal.z * back,
    };
    const side = room && isPointInsidePolygon(behind, room) ? 1 : -1;
    const len = Math.hypot(X(b.x) - X(a.x), Z(b.z) - Z(a.z)) || 1;
    const along = { x: (X(b.x) - X(a.x)) / len, z: (Z(b.z) - Z(a.z)) / len };
    return {
      candidates: slideCandidates(
        X(mid.x) + side * wl.normal.x * WALL_TAG_OFF,
        Z(mid.z) + side * wl.normal.z * WALL_TAG_OFF,
        along,
        len / 2,
        2 * WALL_TAG_R + 2
      ),
      box: (x, y) => circleBox(x, y, WALL_TAG_R + 1),
    };
  });
  const devices = plan.devices || [];
  const marks = devices.map((d) => circleBox(X(d.x), Z(d.z), DEVICE_MARK_R));
  const dotBoxes = dots.map((d) => circleBox(d.px, d.pz, DOT_R));
  const wallSpots = placeLabels(walls, [...dotBoxes, ...marks], frame);

  const obstacles = [
    ...wallSpots.map((p) => circleBox(p.x, p.y, WALL_TAG_R + 1)),
    ...dotBoxes,
    ...marks,
  ];
  const items = (plan.items || []).filter((it) => it.point);
  const labels = [
    ...items.map((it) => ({
      candidates: itemCandidates(X(it.point.x), Z(it.point.z), ITEM_TAG_PX),
      box: (x, y) => textBox(x, y, it.tag, ITEM_TAG_PX),
    })),
    ...devices.map((d, i) => ({
      candidates: ringCandidates(X(d.x), Z(d.z), DEVICE_TAG_AT),
      box: (x, y) => textBox(x, y, d.tag, DEVICE_TAG_PX),
      ignore: [marks[i]],
    })),
  ];
  const spots = placeLabels(labels, obstacles, frame);
  const itemSpot = new Map(items.map((it, i) => [it, spots[i]]));
  return {
    walls: wallSpots,
    items: (plan.items || []).map((it) => itemSpot.get(it) || null),
    devices: spots.slice(items.length),
  };
}

/**
 * Draw a plan as an SVG string, `size` px on its longer side. Framed on the
 * focus room when there is one (a room's plan), else on every outline (a key
 * plan, which also names its rooms). '' for a plan with no outlines.
 *
 * `drawItems(items, view) → [{ key, svg }]` draws the fixed items, in the order
 * they are to be drawn; `drawDevice(device, view) → svg | null` a device, null
 * for one it has no symbol for. The web hands both, so a plan draws its
 * fixtures and devices as the floor plan view does
 * (web-app/src/features/inspection-report/planDrawing.js); without them an
 * item is its plain footprint and a device its own mark. `view` is the plan's
 * pixel frame: `X(x)`, `Z(z)` (metres → px), `s` (px per metre), `minX`,
 * `minZ`, `w` and `h` (the plan's size in px), and the room's `floorY` and
 * `outline`. `drawStructure(structure, view) → svg | null` draws the plan's
 * walls, doors and windows (`plan.structure`) over the rooms' fills and under
 * everything else — the web draws them with the floor plan's own code; without
 * it, or on a plan frozen before plans carried them, the rooms are outlines.
 */
function planSvg(
  plan,
  {
    size = 320,
    interactive = false,
    drawItems = null,
    drawDevice = null,
    drawStructure = null,
  } = {}
) {
  if (!plan || !Array.isArray(plan.outlines) || !plan.outlines.length)
    return '';
  const focus = plan.outlines.filter((o) => o.focus);
  const pts = (focus.length ? focus : plan.outlines).flatMap((o) => o.points);
  // a wall's tag stands outside the room, so the margin must hold it at
  // whatever scale the plan comes out: pad·s ≥ reach, s = size / (span + 2·pad)
  const bare = frameOf(pts, 0);
  const span = Math.max(bare.maxX - bare.minX, bare.maxZ - bare.minZ);
  const pad = (plan.walls || []).length
    ? Math.max(PAD_M, (WALL_TAG_REACH * span) / (size - 2 * WALL_TAG_REACH))
    : PAD_M;
  const { minX, maxX, minZ, maxZ } = frameOf(pts, pad);
  const s = size / Math.max(maxX - minX, maxZ - minZ);
  const X = (x) => (x - minX) * s;
  const Z = (z) => (z - minZ) * s;
  const w = Math.round((maxX - minX) * s);
  const h = Math.round((maxZ - minZ) * s);
  const view = {
    X,
    Z,
    s,
    minX,
    minZ,
    w,
    h,
    floorY: Number.isFinite(plan.floorY) ? plan.floorY : null,
    outline: focus.length ? focus[0].points : null,
  };
  const out = [];
  // on the web page every item, device and dot answers a click (`data-key`),
  // and on a key plan every room the report has a part for
  const keyed = (key, svg) =>
    interactive && key
      ? `<g class="ir-key" data-key="${esc(key)}">${svg}</g>`
      : svg;
  const roomKey = (o) =>
    !focus.length && Number.isInteger(o.room) ? `room:${o.room}` : null;
  // the faint rooms first, so the focus room's edge is drawn over a shared one
  const order = [...plan.outlines].sort(
    (a, b) => Number(a.focus) - Number(b.focus)
  );
  for (const o of order) {
    const bold = o.focus || !focus.length;
    const d = o.points.map((p) => `${f1(X(p.x))},${f1(Z(p.z))}`).join(' ');
    out.push(
      keyed(
        roomKey(o),
        `<polygon points="${d}" fill="${bold ? COLORS.room : COLORS.roomOther}" stroke="${bold ? COLORS.outline : COLORS.outlineOther}" stroke-width="${o.focus ? 2 : 1}"/>`
      )
    );
  }
  const structure =
    plan.structure && drawStructure
      ? drawStructure(plan.structure, view)
      : null;
  if (structure) out.push(structure);
  if (!focus.length) {
    for (const o of plan.outlines) {
      const c = centroid(o.points);
      out.push(
        keyed(
          roomKey(o),
          `<text x="${f1(X(c.x))}" y="${f1(Z(c.z))}" text-anchor="middle" dominant-baseline="central" font-size="12" fill="${COLORS.outline}">${esc(o.name)}</text>`
        )
      );
    }
  }
  for (const f of plan.furniture || []) {
    const d = f.corners.map((p) => `${f1(X(p.x))},${f1(Z(p.z))}`).join(' ');
    out.push(
      `<polygon points="${d}" fill="none" stroke="${COLORS.furniture}" stroke-width="0.75" stroke-dasharray="2 2"/>`
    );
  }
  const items = plan.items || [];
  if (drawItems) {
    for (const { key, svg } of drawItems(items, view))
      out.push(keyed(key, svg));
  } else {
    for (const item of items) {
      for (const box of item.boxes) {
        const d = (box.corners || [])
          .map((p) => `${f1(X(p.x))},${f1(Z(p.z))}`)
          .join(' ');
        out.push(
          keyed(
            item.key,
            `<polygon points="${d}" fill="${COLORS.fixture}" stroke="${COLORS.line}" stroke-width="0.75"/>`
          )
        );
      }
    }
  }
  for (const dev of plan.devices || []) {
    const own = drawDevice ? drawDevice(dev, view) : null;
    out.push(
      keyed(dev.key, own || deviceSvg(dev.letters, X(dev.x), Z(dev.z), COLORS))
    );
  }
  const dots = fanOut(
    (plan.markers || []).map((m) => ({ ...m, tx: X(m.x), tz: Z(m.z) }))
  );
  const room = focus.length ? focus[0].points : null;
  const spots = labelSpots(plan, { X, Z, s, w, h, room, dots });
  const tag = (x, y, text, sizePx) =>
    `<text x="${f1(x)}" y="${f1(y)}" text-anchor="middle" dominant-baseline="central" font-size="${sizePx}" font-weight="700" fill="${COLORS.tag}" stroke="${COLORS.white}" stroke-width="2.5" paint-order="stroke">${esc(text)}</text>`;
  (plan.items || []).forEach((item, i) => {
    const at = spots.items[i];
    if (at) out.push(keyed(item.key, tag(at.x, at.y, item.tag, ITEM_TAG_PX)));
  });
  (plan.devices || []).forEach((dev, i) => {
    const at = spots.devices[i];
    // a tag pushed well off its device keeps a hairline back to it
    const far = Math.hypot(at.x - X(dev.x), at.y - Z(dev.z)) > LEADER_PX;
    const leader = far
      ? `<line x1="${f1(X(dev.x))}" y1="${f1(Z(dev.z))}" x2="${f1(at.x)}" y2="${f1(at.y)}" stroke="${COLORS.tag}" stroke-width="0.5"/>`
      : '';
    out.push(keyed(dev.key, leader + tag(at.x, at.y, dev.tag, DEVICE_TAG_PX)));
  });
  // A named wall: the wall drawn heavy, and its letter in a tag at the spot
  // `labelSpots` chose — outside the room, clear of the fixtures along it.
  // One key, so a hovered row lights the whole wall and a click on either
  // finds the wall's rows.
  (plan.walls || []).forEach((wl, i) => {
    const [a, b] = wl.ends;
    const { x: mx, y: mz } = spots.walls[i];
    out.push(
      keyed(
        `wall:${wl.letter}`,
        `<line x1="${f1(X(a.x))}" y1="${f1(Z(a.z))}" x2="${f1(X(b.x))}" y2="${f1(Z(b.z))}" stroke="${COLORS.wall}" stroke-width="4" stroke-linecap="round"/>` +
          `<circle cx="${f1(mx)}" cy="${f1(mz)}" r="${WALL_TAG_R}" fill="${COLORS.white}" stroke="${COLORS.wall}" stroke-width="1.5"/>` +
          `<text x="${f1(mx)}" y="${f1(mz)}" text-anchor="middle" dominant-baseline="central" font-size="11" font-weight="700" fill="${COLORS.wall}">${esc(wl.letter)}</text>`
      )
    );
  });
  for (const d of dots) {
    if (d.px !== d.tx || d.pz !== d.tz) {
      out.push(
        `<line x1="${f1(d.tx)}" y1="${f1(d.tz)}" x2="${f1(d.px)}" y2="${f1(d.pz)}" stroke="${COLORS.outline}" stroke-width="1"/>`
      );
    }
    // a severity outside the vocabulary (never written by the service) is
    // drawn in the text colour rather than dropped
    const fill = COLORS.severity[d.severity] || COLORS.outline;
    out.push(
      keyed(
        `defect:${d.n}`,
        `<circle cx="${f1(d.px)}" cy="${f1(d.pz)}" r="${DOT_R}" fill="${fill}"/>` +
          `<text x="${f1(d.px)}" y="${f1(d.pz)}" text-anchor="middle" dominant-baseline="central" font-size="10" font-weight="700" fill="${COLORS.white}">${esc(d.n)}</text>`
      )
    );
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" font-family="Helvetica, Arial, sans-serif">${out.join('')}</svg>`;
}

module.exports = {
  // the plans' palette, for the web's drawers (planDrawing.js)
  PLAN_COLORS: COLORS,
  centroid,
  turnFromTop,
  roomOutline,
  hasFace,
  letterWalls,
  keyPlan,
  roomPlan,
  planSvg,
};
